import { Crown, Sparkles } from "lucide-react";
import useReveal from "../hooks/useReveal";

const CALENDLY = "https://calendly.com/erodwentpro/rideshare-booking";
const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/b84d0196-ce96-4816-ad90-5ea3362a6c1d/images/6e698dc58a54b363716414747a0c7f3992951d84b563c2d10cfab94d180b863c.png";

export const Hero = () => {
  const r = useReveal();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full overflow-hidden flex items-center"
    >
      {/* Background image with slow zoom */}
      <div className="absolute inset-0 z-0">
        <img
          src={HERO_BG}
          alt="Elegant black luxury car in a moody nighttime cityscape"
          className="w-full h-full object-cover hero-zoom"
        />
      </div>
      {/* Multi-layer overlay for legibility */}
      <div className="absolute inset-0 z-10 bg-gradient-to-b from-[#05030A]/85 via-[#1A0B2E]/55 to-[#05030A]" />
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-[#05030A]/70 via-transparent to-[#05030A]/30" />

      {/* Decorative gold corner lines */}
      <div className="absolute top-24 left-6 sm:left-10 z-20 w-16 h-px bg-[#D4AF37]/60" />
      <div className="absolute bottom-10 right-6 sm:right-10 z-20 w-16 h-px bg-[#D4AF37]/60" />

      <div
        ref={r}
        className="reveal relative z-20 max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-20 w-full"
      >
        <div className="max-w-3xl">
          <div
            data-testid="hero-overline"
            className="flex items-center gap-3 mb-7"
          >
            <Crown
              className="w-4 h-4 text-[#D4AF37]"
              strokeWidth={1.5}
            />
            <span className="font-sans text-[11px] sm:text-xs uppercase tracking-[0.32em] text-[#D4AF37] font-medium">
              Private · Safe · Luxury Rides for Women
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-white font-medium"
          >
            Royal Rides{" "}
            <span className="italic gold-text">Queen</span>
          </h1>

          <p
            data-testid="hero-tagline"
            className="font-serif italic text-2xl sm:text-3xl text-[#EAE6DF] mt-6 leading-snug"
          >
            Ride in peace. Arrive in power.
          </p>

          <p
            data-testid="hero-subtext"
            className="font-sans text-base sm:text-lg text-[#A89FBB] font-light mt-5 max-w-xl leading-relaxed"
          >
            No awkward energy. No stress. No compromise. A discreet,
            women-first experience designed around your comfort and
            calm — every mile of the way.
          </p>

          {/* Feature pills */}
          <ul className="flex flex-wrap gap-x-6 gap-y-3 mt-8">
            {[
              "Flat-Rate Pricing",
              "Late-Night Availability",
              "Discreet, Luxury Service",
            ].map((f) => (
              <li
                key={f}
                className="flex items-center gap-2 text-sm text-[#EAE6DF] font-light"
              >
                <Sparkles
                  className="w-3.5 h-3.5 text-[#D4AF37]"
                  strokeWidth={1.5}
                />
                {f}
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href={CALENDLY}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-book-now-button"
              className="group inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#05030A] font-sans font-medium px-9 py-4 text-base uppercase tracking-[0.18em] border border-[#D4AF37] hover:bg-[#F3E5AB] hover:shadow-[0_0_36px_rgba(212,175,55,0.45)] transition-all duration-300"
            >
              Book Your Ride Now
              <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">
                →
              </span>
            </a>
            <a
              href="#pricing"
              data-testid="hero-view-pricing-link"
              className="inline-flex items-center justify-center px-7 py-4 text-sm uppercase tracking-[0.22em] text-[#EAE6DF] border border-white/15 hover:border-[#D4AF37]/60 hover:text-[#D4AF37] transition-all duration-300"
            >
              View Pricing
            </a>
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#A89FBB]">
        <span>Scroll</span>
        <span className="w-px h-8 bg-gradient-to-b from-[#D4AF37] to-transparent" />
      </div>
    </section>
  );
};
